import { getVersion } from "@tauri-apps/api/app";
import { isTauri } from "@tauri-apps/api/core";
import { relaunch } from "@tauri-apps/plugin-process";
import { check, type DownloadEvent, type Update } from "@tauri-apps/plugin-updater";
import { Download, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

export const UPDATE_MANUAL_CHECK_EVENT = "dripwatch-check-updates";

/** Use from Account (or elsewhere) to run the same check+download flow as startup. */
export function requestManualAppUpdateCheck(): void {
  window.dispatchEvent(new Event(UPDATE_MANUAL_CHECK_EVENT));
}

type Phase =
  | "idle"
  | "checking"
  | "downloading"
  | "downloaded"
  | "installing"
  | "uptodate"
  | "latestAssumed"
  | "error";

/** When the update server has no valid manifest (e.g. missing latest.json), treat as up to date. */
function isMissingRemoteManifestError(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes("valid release json") || m.includes("could not fetch a valid release");
}

function shouldRunStartupUpdater(): boolean {
  if (!isTauri()) return false;
  if (import.meta.env.PROD) return true;
  return import.meta.env.VITE_TAURI_TEST_UPDATER === "true";
}

function attachDownloadProgress(
  setDownloadPct: (n: number | null) => void,
): (event: DownloadEvent) => void {
  let downloaded = 0;
  let total = 0;
  return (event: DownloadEvent) => {
    if (event.event === "Started") {
      total = event.data.contentLength ?? 0;
      setDownloadPct(0);
    } else if (event.event === "Progress") {
      downloaded += event.data.chunkLength;
      if (total > 0) {
        setDownloadPct(Math.min(100, Math.round((downloaded / total) * 100)));
      }
    } else if (event.event === "Finished") {
      setDownloadPct(100);
    }
  };
}

export function UpdateChecker() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [availableVersion, setAvailableVersion] = useState<string | null>(null);
  const [notes, setNotes] = useState<string | null>(null);
  const [downloadPct, setDownloadPct] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pendingUpdateRef = useRef<Update | null>(null);
  /** Increment to invalidate in-flight download (StrictMode unmount, new manual check). */
  const flowGenerationRef = useRef(0);

  const dismissDownloaded = useCallback(async () => {
    setPhase("idle");
    setAvailableVersion(null);
    setNotes(null);
    setDownloadPct(null);
    const u = pendingUpdateRef.current;
    pendingUpdateRef.current = null;
    flowGenerationRef.current += 1;
    if (u) {
      try {
        await u.close();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const runCheckDownloadFlow = useCallback(async () => {
    flowGenerationRef.current += 1;
    const flowId = flowGenerationRef.current;

    const stale = () => flowId !== flowGenerationRef.current;

    if (pendingUpdateRef.current) {
      try {
        await pendingUpdateRef.current.close();
      } catch {
        /* ignore */
      }
      pendingUpdateRef.current = null;
    }

    setError(null);
    setPhase("checking");
    setDownloadPct(null);

    try {
      const ver = await getVersion();
      if (stale()) return;
      setCurrentVersion(ver);

      const update = await check();
      if (stale()) {
        if (update) void update.close().catch(() => {});
        return;
      }

      if (!update) {
        setPhase("uptodate");
        return;
      }

      setAvailableVersion(update.version);
      setNotes(update.body ?? null);
      setPhase("downloading");
      pendingUpdateRef.current = update;

      await update.download(attachDownloadProgress(setDownloadPct));

      if (stale()) {
        void update.close().catch(() => {});
        if (pendingUpdateRef.current === update) pendingUpdateRef.current = null;
        return;
      }

      setPhase("downloaded");
    } catch (e) {
      if (stale()) return;
      const msg = e instanceof Error ? e.message : String(e);
      if (isMissingRemoteManifestError(msg)) {
        console.warn("Updater: no remote manifest, assuming latest:", e);
        setError(null);
        setPhase("latestAssumed");
      } else {
        console.warn("Updater:", e);
        setError(msg);
        setPhase("error");
      }
      const u = pendingUpdateRef.current;
      pendingUpdateRef.current = null;
      if (u) {
        try {
          await u.close();
        } catch {
          /* ignore */
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!shouldRunStartupUpdater()) return;
    void runCheckDownloadFlow();
    return () => {
      flowGenerationRef.current += 1;
    };
  }, [runCheckDownloadFlow]);

  useEffect(() => {
    const onManual = () => {
      void runCheckDownloadFlow();
    };
    window.addEventListener(UPDATE_MANUAL_CHECK_EVENT, onManual);
    return () => window.removeEventListener(UPDATE_MANUAL_CHECK_EVENT, onManual);
  }, [runCheckDownloadFlow]);

  useEffect(() => {
    if (phase !== "latestAssumed") return;
    const t = window.setTimeout(() => setPhase("idle"), 4500);
    return () => window.clearTimeout(t);
  }, [phase]);

  const runInstall = useCallback(async () => {
    const update = pendingUpdateRef.current;
    if (!update) return;
    setError(null);
    setPhase("installing");
    try {
      await update.install();
      await relaunch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase("downloaded");
    }
  }, []);

  if (!isTauri()) return null;

  if (phase === "uptodate" || phase === "idle") {
    return null;
  }

  const showBanner =
    phase === "checking" ||
    phase === "downloading" ||
    phase === "downloaded" ||
    phase === "installing" ||
    phase === "latestAssumed" ||
    phase === "error";

  if (!showBanner) return null;

  return (
    <div
      className="sticky top-0 z-20 border-b border-accent/40 bg-surface-1 px-4 py-3 shadow-md shadow-accent/10 backdrop-blur-md md:px-8"
      role="region"
      aria-label="App update"
    >
      {phase === "checking" && (
        <p className="text-center text-sm text-text-muted">Checking for updates…</p>
      )}

      {phase === "downloading" && availableVersion && (
        <p className="text-center text-sm text-text">
          Downloading version <span className="font-semibold">{availableVersion}</span>
          {currentVersion && (
            <span className="text-text-muted"> · you have {currentVersion}</span>
          )}
          {downloadPct !== null && (
            <span className="text-text-muted"> · {downloadPct}%</span>
          )}
        </p>
      )}

      {phase === "downloaded" && availableVersion && (
        <div className="mx-auto flex max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0 text-sm">
            <p className="font-display font-semibold text-text">Update ready to install</p>
            <p className="mt-1 text-text-muted">
              Version <span className="font-medium text-text">{availableVersion}</span> has been
              downloaded
              {currentVersion ? (
                <>
                  {" "}
                  (you are on <span className="text-text">{currentVersion}</span>)
                </>
              ) : null}
              . Choose <strong className="text-text">Install and restart</strong> to run the
              updater.
            </p>
            {notes && (
              <p className="mt-2 whitespace-pre-wrap text-xs text-text-muted">{notes}</p>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void runInstall()}
              className="inline-flex items-center gap-2 rounded-xl bg-accent px-4 py-2.5 text-sm font-semibold text-surface-0 shadow-lg shadow-accent/25 hover:brightness-110"
            >
              <Download className="h-4 w-4" aria-hidden />
              Install and restart
            </button>
            <button
              type="button"
              onClick={() => void dismissDownloaded()}
              className="rounded-xl border border-border px-3 py-2 text-sm text-text-muted hover:bg-surface-2"
            >
              Later
            </button>
          </div>
        </div>
      )}

      {phase === "installing" && (
        <p className="text-center text-sm text-text-muted">
          Running the installer… The app will restart when ready.
        </p>
      )}

      {phase === "latestAssumed" && (
        <p className="text-center text-sm text-text">
          You have the latest version!
          {currentVersion ? (
            <span className="text-text-muted"> ({currentVersion})</span>
          ) : null}
        </p>
      )}

      {phase === "error" && (
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-3 sm:flex-row sm:justify-between">
          <p className="text-center text-sm text-danger" role="alert">
            {error ?? "Update failed"}
          </p>
          <button
            type="button"
            onClick={() => void runCheckDownloadFlow()}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm text-text hover:bg-surface-2"
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Try again
          </button>
        </div>
      )}
    </div>
  );
}
